// Properties CRM read layer. A "property" is a real-estate tour that has a
// `listing` row (tours.listing_id). This is a CRM *lens* over those tours — it
// reuses v_tours (listing already embedded) and joins each property to its
// leads / bookings / interested contacts. No new read schema.

import { supabase } from "@cl/supabase";
import { fetchTourById, fetchUserTours } from "@a/tours";
import { fetchTourLeads, type TourLead } from "@a/leads";
import { deriveInterestedContacts } from "@u/propertyRelated";
import type { VTour } from "@/types";
import type { Booking } from "@/types/booking.types";

export interface CrmProperty {
  tour: VTour; // includes embedded `listing`
  leadCount: number;
  bookingCount: number;
}

/** Minimal contact shape for the property "interested" list (the `contacts`
 *  table lacks the derived stage/products of the CRM view). */
export interface InterestedContact {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  /** True when reached via a lead/booking on this property (vs a manual tag). */
  origin: boolean;
}

export interface PropertyDetail {
  tour: VTour;
  leads: TourLead[];
  bookings: Booking[];
  contacts: InterestedContact[];
}

/** All of an owner's properties (real-estate tours with a listing) + counts. */
export async function fetchCrmProperties(
  ownerId: string,
): Promise<CrmProperty[]> {
  // A "property" = a tour with a `listing` row. The real link is `listing.tour_id`
  // (what the editor writes + what v_tours embeds); the forward FK `tours.listing_id`
  // is never populated, so filter on the embedded listing, not that dead column.
  // Owner-tours read is shared with fetchUserTours (single source).
  const tours = (await fetchUserTours(ownerId)).filter((t) => t.listing != null);
  if (tours.length === 0) return [];

  const ids = tours.map((t) => t.id);
  // Server-side aggregate (RPC) — the old client-side tally fetched every lead
  // and booking row and counted in JS, silently undercounting above the
  // 1000-row REST cap for a busy agency.
  const { data: counts, error: cErr } = await supabase.rpc(
    "property_engagement_counts",
    { p_tour_ids: ids },
  );
  if (cErr) throw cErr;

  const byTour = new Map<string, { lead_count: number; booking_count: number }>();
  for (const r of (counts ?? []) as {
    tour_id: string;
    lead_count: number;
    booking_count: number;
  }[]) {
    byTour.set(r.tour_id, r);
  }

  return tours.map((tour) => ({
    tour,
    leadCount: byTour.get(tour.id)?.lead_count ?? 0,
    bookingCount: byTour.get(tour.id)?.booking_count ?? 0,
  }));
}

/** One property + its related leads, bookings and interested contacts. */
export async function fetchPropertyDetail(
  tourId: string,
): Promise<PropertyDetail> {
  const [tour, leads, bookingsRes, manualRes] = await Promise.all([
    fetchTourById(tourId),
    fetchTourLeads(tourId),
    supabase
      .from("bookings")
      .select("*")
      .eq("tour_id", tourId)
      .order("start_at", { ascending: false }),
    // Interested contacts = those with an opportunity (deal) on this property.
    supabase
      .from("deals")
      .select("contact:contacts(id, name, email, phone)")
      .eq("tour_id", tourId),
  ]);
  if (bookingsRes.error) throw bookingsRes.error;
  if (manualRes.error) throw manualRes.error;
  const bookings = (bookingsRes.data ?? []) as Booking[];

  // Origin contacts: those reached through this property's leads/bookings.
  const originIds = new Set<string>();
  const [leadContactsRes, bookingContactsRes] = await Promise.all([
    supabase
      .from("tour_leads")
      .select("contact_id")
      .eq("tour_id", tourId)
      .not("contact_id", "is", null),
    supabase
      .from("bookings")
      .select("contact_id")
      .eq("tour_id", tourId)
      .not("contact_id", "is", null),
  ]);
  for (const r of leadContactsRes.data ?? [])
    if (r.contact_id) originIds.add(r.contact_id as string);
  for (const r of bookingContactsRes.data ?? [])
    if (r.contact_id) originIds.add(r.contact_id as string);

  let originContacts: InterestedContact[] = [];
  if (originIds.size > 0) {
    const { data } = await supabase
      .from("contacts")
      .select("id, name, email, phone")
      .in("id", [...originIds]);
    originContacts = (data ?? []).map((c) => ({
      ...(c as Omit<InterestedContact, "origin">),
      origin: true,
    }));
  }
  type BareContact = Omit<InterestedContact, "origin">;
  const manualContacts: InterestedContact[] = (manualRes.data ?? [])
    .map((r) => {
      const cc = (r as { contact: BareContact | BareContact[] | null }).contact;
      return Array.isArray(cc) ? cc[0] ?? null : cc;
    })
    .filter((c): c is BareContact => !!c)
    .map((c) => ({ ...c, origin: false }));

  return {
    tour,
    leads,
    bookings,
    contacts: deriveInterestedContacts(originContacts, manualContacts),
  };
}
